import React from 'react';
import { Box, InputBase, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { tokens } from '../../../../../theme/tokens';
import { CurrencyTextInput } from '../../../../../components/common/CurrencyTextInput';
import { FieldSource } from './fieldSources';

/**
 * The compact "client details" presentation (v3 mockup), shared by the Client
 * Details tab and the Add Client flow so the two read as one design: sections
 * with an icon tile, per-person column headers, and label | value | dot | date
 * rows that swap in a slim navy-bordered editor while editing.
 */

export const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// VM dates are 'YYYY-MM-DD' — anchor to local midnight so the display never
// shifts a day across timezones.
export const fmtDate = (d?: string | null): string =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// Mockup NumInput chrome: 110 wide, "2px 6px", 1.5px navy border, radius 5.
export const editorInputSx = {
  width: 110,
  px: '6px',
  py: '2px',
  fontSize: 12.5,
  fontWeight: 600,
  color: tokens.text,
  fontFamily: 'inherit',
  bgcolor: '#fff',
  border: `1.5px solid ${tokens.navy}`,
  borderRadius: '5px',
};

/**
 * Mockup name-edit input rendered inside the ColHeader slot (full width).
 * Deliberately NOT uppercase like the surrounding ColHeader: the advisor must be
 * able to see the casing they are typing ("Jane smith" vs "Jane Smith") because
 * names are stored exactly as entered.
 */
export const nameEditorSx = {
  flex: 1,
  minWidth: 0,
  px: '6px',
  py: '2px',
  fontSize: 11,
  fontWeight: 600,
  color: tokens.text,
  fontFamily: 'inherit',
  bgcolor: '#fff',
  border: `1.5px solid ${tokens.border}`,
  borderRadius: '6px',
  '& input': { p: 0 },
};

/** Full-width sub-block below the person grid (mockup "Household" strip). */
export const householdBlockSx = { mt: '10px', pt: '10px', borderTop: `1px solid ${tokens.border}55` };

/** Two-column per-person layout: columns separated by a 28px gutter, not a divider. */
export const personGridSx = (twoColumns: boolean) =>
  ({
    display: 'grid',
    gridTemplateColumns: twoColumns ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
    columnGap: '28px',
  }) as const;

/* ── Compact row (mockup grid): label | value/editor | dot | updated ────── */

export interface CompactRowProps {
  label: string;
  control: 'date' | 'number' | 'currency' | 'percent' | 'select' | 'text' | 'readonly';
  value: any;
  onChange?: (v: any) => void;
  options?: { value: string; label: string }[];
  suffix?: string;
  editing: boolean;
  source?: FieldSource | null;
  updated?: string;
  /** Placeholder for the editor when the value is empty (Add Client). */
  placeholder?: string;
  /** Marks the label with an asterisk — intake forms only. */
  required?: boolean;
  /** Validation message rendered under the row; also outlines the editor. */
  error?: string;
  onBlur?: () => void;
}

const ERROR_RED = '#DC2626';

export const CompactRow: React.FC<CompactRowProps> = ({
  label,
  control,
  value,
  onChange,
  options,
  suffix,
  editing,
  source,
  updated,
  placeholder,
  required,
  error,
  onBlur,
}) => {
  const editable = editing && control !== 'readonly' && !!onChange;
  const errorSx = error ? { borderColor: ERROR_RED } : {};

  const display = () => {
    if (value == null || value === '') return '—';
    switch (control) {
      case 'currency':
        return currencyFmt.format(Math.round(Number(value)));
      case 'percent':
        return `${value}%`;
      case 'date':
        return fmtDate(value);
      case 'select':
        return options?.find((o) => o.value === String(value))?.label ?? String(value);
      default:
        return `${value}${suffix ? ` ${suffix}` : ''}`;
    }
  };

  const editor = () => {
    if (control === 'select') {
      return (
        <Select
          size="small"
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange!(e.target.value)}
          sx={{
            minWidth: 150,
            borderRadius: '5px',
            bgcolor: '#fff',
            fontSize: 12.5,
            fontWeight: 600,
            '& .MuiOutlinedInput-notchedOutline': { border: `1.5px solid ${tokens.navy}` },
          }}
        >
          {(options || []).map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      );
    }
    if (control === 'currency') {
      // Text input so the value keeps thousands separators while editing.
      // Whole dollars only — stored values can carry float noise from
      // estimate math; never surface it. The $ sits outside the box (mockup).
      return (
        <>
          <Typography component="span" sx={{ fontSize: 12, color: tokens.textSec }}>
            $
          </Typography>
          <CurrencyTextInput
            placeholder={placeholder}
            value={value == null ? null : Number(value)}
            showZeroAsEmpty={!!placeholder}
            emptyValue={null}
            onChange={(n) => onChange!(n)}
            onBlur={onBlur}
            inputProps={{ style: { textAlign: 'right', padding: 0 } }}
            sx={{ ...editorInputSx, ...errorSx }}
          />
        </>
      );
    }
    return (
      <InputBase
        type={control === 'date' ? 'date' : control === 'text' ? 'text' : 'number'}
        placeholder={placeholder}
        value={control === 'number' ? (value == null || (value === 0 && placeholder) ? '' : Math.round(Number(value))) : value ?? ''}
        onChange={(e) => {
          if (control === 'date' || control === 'text') return onChange!(e.target.value || null);
          if (e.target.value === '') return onChange!(null);
          const n = Number(e.target.value);
          onChange!(control === 'percent' ? n : Math.round(n));
        }}
        onBlur={onBlur}
        inputProps={{
          min: 0,
          style: { textAlign: control === 'date' || control === 'text' ? 'left' : 'right', padding: 0 },
        }}
        sx={{ ...editorInputSx, ...errorSx, width: control === 'text' ? 210 : control === 'date' ? 140 : 110 }}
      />
    );
  };

  return (
    <Box sx={{ borderBottom: `1px solid ${tokens.border}55` }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto auto',
          alignItems: 'center',
          gap: '10px',
          py: '5px',
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 400, color: tokens.textSec, minWidth: 0 }}>
          {label}
          {required && (
            <Box component="span" sx={{ color: ERROR_RED, ml: '2px' }}>
              *
            </Box>
          )}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {editable ? (
            editor()
          ) : (
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 700,
                color: tokens.text,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {display()}
            </Typography>
          )}
        </Box>
        {/* Source dot sits between the value and the date (mockup placement). */}
        {source ? (
          <Tooltip title={source.label}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: source.color, flexShrink: 0 }} />
          </Tooltip>
        ) : (
          <Box sx={{ width: 6, flexShrink: 0 }} />
        )}
        <Typography sx={{ fontSize: 10, color: tokens.textMut, width: 68, textAlign: 'right', flexShrink: 0 }}>
          {updated || ''}
        </Typography>
      </Box>
      {error && (
        <Typography sx={{ fontSize: 10.5, color: ERROR_RED, pb: '5px', mt: '-2px' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

/* ── Compact collapsible section ───────────────────────────────────────── */

export const CompactSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  estimateCount?: number;
  open: boolean;
  onToggle?: () => void;
  /** false = always open, no chevron, header isn't a button (Add Client). */
  collapsible?: boolean;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, estimateCount = 0, open, onToggle, collapsible = true, children }) => {
  const isOpen = collapsible ? open : true;
  return (
    <Box sx={{ bgcolor: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: '9px', overflow: 'hidden' }}>
      <Box
        onClick={collapsible ? onToggle : undefined}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: '14px',
          py: '9px',
          bgcolor: isOpen ? tokens.card : tokens.bg,
          borderBottom: isOpen ? `1px solid ${tokens.border}` : 'none',
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none',
          transition: 'background 0.12s',
        }}
      >
        {/* 22px lightBlue icon tile (mockup) */}
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '5px',
            bgcolor: tokens.lightBlue,
            color: tokens.navy,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: tokens.text }}>
            {title}
          </Typography>
          <Typography component="span" sx={{ fontSize: 11.5, color: tokens.textMut, ml: 1 }}>
            {subtitle}
          </Typography>
        </Box>
        {!isOpen && estimateCount > 0 && (
          <Typography sx={{ fontSize: 10.5, color: tokens.textMut, fontStyle: 'italic', flexShrink: 0 }}>
            {estimateCount} estimated
          </Typography>
        )}
        {collapsible && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 12 12"
            style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}
          >
            <path d="M2 4l4 4 4-4" stroke={tokens.textSec} strokeWidth="1.5" strokeLinecap="round" fill="none" />
          </svg>
        )}
      </Box>
      {isOpen && <Box sx={{ px: '14px', py: '11px' }}>{children}</Box>}
    </Box>
  );
};

/** Per-person column header inside a section (mockup ColH — uppercase navy, lightBlue underline). */
export const ColHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      fontSize: 10.5,
      fontWeight: 700,
      color: tokens.navy,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      mb: '5px',
      pb: '4px',
      borderBottom: `1.5px solid ${tokens.lightBlue}`,
    }}
  >
    {children}
  </Box>
);
